import React, {
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useConnection } from '../../connection/connection';
import { useBusinessContext } from '../../context/BusinessContext';
import Form, { FormSchema } from '@sezenta/antd-schema-form';
import { useEvents } from '../../common/EventContext';
import { Expense } from '../../dto/Expense';
import { Button, Drawer } from 'antd';
import dayjs from 'dayjs';

export type ExpenseCreateModalInstance = {
  create: () => void;
  edit: (expense: Expense) => void;
};

type Props = {};

const ExpenseCreateModal: ForwardRefRenderFunction<
  ExpenseCreateModalInstance,
  Props
> = (props, ref) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [expense, setExpense] = useState<Expense>();
  const connection = useConnection();
  const { businessId, select, accounts } = useBusinessContext();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState<boolean>(false);
  const events = useEvents();
  const [mode, setMode] = useState<'create' | 'edit'>('create');

  useImperativeHandle(
    ref,
    () => ({
      create: () => {
        setVisible(true);
        form.resetFields();
        setMode('create');
      },
      edit: (expense: Expense) => {
        setVisible(true);
        form.resetFields();
        form.setFieldsValue(expense);
        setExpense(expense);
        setMode('edit');
      },
    }),
    [form],
  );

  const schema: FormSchema = [
    {
      id: 'type',
      name: 'Expense Type',
      type: 'select',
      props: {
        options: select.expenseTypes ?? [],
        //showSearch: true,
      },
      options: {
        rules: [{ required: true, message: 'Expense Type is required' }],
      },
    },
    {
      id: 'site',
      name: 'Site',
      type: 'site',
      options: {
        rules: [{ required: true, message: 'Site is required' }],
      },
    },
    {
      id: 'account',
      type: 'select2',
      name: 'Account',
      layout: { xs: 12 },
      props: (values: any) => {
        return {
          showSearch: true,
          optionFilterProp: 'label',
          options: Object.values(accounts)
            .filter((acc) => acc.activity)
            // .filter((acc) => !acc.siteId || acc.siteId === values.siteId)
            .map((acc) => ({ label: acc.name, value: acc.accountId })),
        };
      },
      options: {
        rules: [{ required: true, message: 'Account is required' }],
      },
    },
    {
      id: 'refNo',
      name: 'Ref Number',
      type: 'string',
      layout: { xs: 12 },
      options: {
        rules: [{ required: true, message: 'Ref number is required' }],
      },
    },
    {
      id: 'amount',
      name: 'Net Amount',
      type: 'price',
      options: {
        rules: [{ required: true, message: 'Amount is required' }],
      },
    },
    {
      id: 'date',
      name: 'Date',
      type: 'date',
      options: {
        rules: [{ required: true, message: 'Amount is required' }],
      },
    },
    {
      id: 'vatType',
      type: 'select2',
      name: 'Vat',
      layout: { xs: 12 },
      props: {
        options: [
          { label: 'Apply VAT', value: 'Apply VAT' },
          { label: 'Apply SVAT', value: 'Apply SVAT' },
          { label: 'No VAT', value: 'No VAT' },
        ],
      },
      options: {
        rules: [{ required: true, message: 'Vat is required' }],
      },
    },
    {
      id: 'vatAmount',
      type: 'price',
      name: 'Vat Amount',
      layout: { xs: 12 },
      visible: (v) => v.vatType && v.vatType !== 'No VAT',
      props: {
        placeholder: 'Vat amount',
        min: 0,
      },
      options: {
        rules: [{ required: true, message: 'Vat amount is required' }],
      },
    },
    {
      id: 'payee',
      name: 'Payee',
      type: 'string',
    },
    {
      id: 'chequeNo',
      name: 'Cheque Number',
      type: 'string',
    },
    {
      id: 'voucher',
      name: 'Voucher',
      type: 'string',
    },
    {
      id: 'description',
      name: 'Description',
      type: 'text',
    },
  ];

  const submit = useCallback(
    (value: Omit<Expense, 'expenseId' | 'businessId'>) => {
      setSaving(true);
      if (mode === 'create') {
        console.log(value);
        connection
          .post(`expenses/${businessId}`, {
            ...value,
            date: dayjs(value.date).format('YYYY-MM-DD'),
          })
          .then((res) => {
            events.emit('EXPENSE', { action: 'CREATE', data: res.data });
            setVisible(false);
          })
          .catch((err) => {
            console.log('ERRRRRRRR', err);
          })
          .finally(() => setSaving(false));
      } else {
        connection
          .patch(`expenses/${businessId}/${expense?.expenseId}`, value)
          .then((res) => {
            events.emit('EXPENSE', { action: 'UPDATE', data: res.data });
            setVisible(false);
          })
          .finally(() => setSaving(false));
      }
    },
    [businessId, connection, events, mode, expense],
  );

  return (
    <Drawer
      title={mode === 'create' ? 'Create a Expense' : 'Edit Expense'}
      width={600}
      open={visible}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={() => setVisible(false)}>Cancel</Button>
          <Button loading={saving} type="primary" onClick={() => form.submit()}>
            Save
          </Button>
        </div>
      }
      onClose={() => setVisible(false)}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Items schema={schema} />
      </Form>
    </Drawer>
  );
};

export const useExpenseCreateModal = () =>
  useRef<ExpenseCreateModalInstance>(null);

export default React.forwardRef<ExpenseCreateModalInstance, Props>(
  ExpenseCreateModal,
);
